/**
 * Tooltip — 9º componente canônico do design system (FASE-C).
 *
 * Motivo (achado do Matheus, 30/07): os balões de ajuda eram desenhados com
 * `position:absolute` DENTRO do próprio container — qualquer card/tabela com
 * `overflow` cortava a caixa, e na borda direita o balão estourava a viewport.
 *
 * Regras deste componente (DoD do handoff):
 *   - Renderiza em PORTAL (document.body) — nunca clipado por overflow.
 *   - `position: fixed` calculado do rect do gatilho, com FLIP vertical
 *     automático (abre pra cima; sem espaço → pra baixo, e vice-versa) e
 *     SHIFT horizontal (clampa na viewport com 8px de folga).
 *   - max-width 280px no desktop / 90vw no mobile, com quebra de linha.
 *   - Abre no hover E no foco (teclado) E no toque (click alterna).
 *   - Fecha com Esc, blur, mouseleave; reposiciona em scroll/resize.
 *   - `role="tooltip"` + `aria-describedby` no gatilho.
 *
 * Uso direto (gatilho customizado) — o HelpTip ("?") é o wrapper padrão:
 *   <Tooltip content="Explicação">
 *     <button>…</button>
 *   </Tooltip>
 */
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/cn';
import { CAMADA } from '@/lib/camadas';

const FOLGA = 8; // px de respiro da borda da viewport

interface Pos {
  top: number;
  left: number;
  maxWidth: number;
}

export function Tooltip({
  content,
  children,
  lado = 'cima',
  className,
}: {
  content: ReactNode;
  /** Gatilho — um único elemento (recebe handlers + aria-describedby). */
  children: ReactElement<Record<string, unknown>>;
  /** Preferência inicial; o flip automático pode inverter. */
  lado?: 'cima' | 'baixo';
  className?: string;
}) {
  const id = useId();
  const [aberto, setAberto] = useState(false);
  const [fixado, setFixado] = useState(false);
  const [pos, setPos] = useState<Pos | null>(null);
  const gatilhoRef = useRef<HTMLElement | null>(null);
  const balaoRef = useRef<HTMLDivElement | null>(null);

  const visivel = aberto || fixado;

  const reposicionar = useCallback(() => {
    const gatilho = gatilhoRef.current;
    const balao = balaoRef.current;
    if (!gatilho || !balao) return;
    const r = gatilho.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxWidth = Math.min(280, vw * 0.9);
    // mede com o max-width aplicado
    balao.style.maxWidth = `${maxWidth}px`;
    const b = balao.getBoundingClientRect();
    // FLIP vertical: preferência 'cima'; sem espaço, vai pra baixo (e vice-versa).
    const cabeEmCima = r.top - b.height - 6 >= FOLGA;
    const cabeEmBaixo = r.bottom + b.height + 6 <= vh - FOLGA;
    const emCima = lado === 'cima' ? cabeEmCima || !cabeEmBaixo : !cabeEmBaixo && cabeEmCima;
    const top = emCima ? r.top - b.height - 6 : r.bottom + 6;
    // SHIFT horizontal: centraliza no gatilho e clampa na viewport.
    let left = r.left + r.width / 2 - b.width / 2;
    left = Math.max(FOLGA, Math.min(left, vw - b.width - FOLGA));
    setPos({ top: Math.max(FOLGA, top), left, maxWidth });
  }, [lado]);

  useLayoutEffect(() => {
    if (!visivel) {
      setPos(null);
      return;
    }
    reposicionar();
  }, [visivel, reposicionar]);

  useEffect(() => {
    if (!visivel) return;
    const fechar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAberto(false);
        setFixado(false);
      }
    };
    window.addEventListener('scroll', reposicionar, true);
    window.addEventListener('resize', reposicionar);
    window.addEventListener('keydown', fechar);
    return () => {
      window.removeEventListener('scroll', reposicionar, true);
      window.removeEventListener('resize', reposicionar);
      window.removeEventListener('keydown', fechar);
    };
  }, [visivel, reposicionar]);

  if (!isValidElement(children)) return children;

  const original = children.props as Record<string, unknown>;
  const gatilho = cloneElement(children, {
    'aria-describedby': visivel ? id : undefined,
    ref: (el: HTMLElement | null) => {
      gatilhoRef.current = el;
      const r = (children as { ref?: unknown }).ref;
      if (typeof r === 'function') r(el);
      else if (r && typeof r === 'object') (r as { current: HTMLElement | null }).current = el;
    },
    onMouseEnter: (e: unknown) => {
      (original.onMouseEnter as ((e: unknown) => void) | undefined)?.(e);
      setAberto(true);
    },
    onMouseLeave: (e: unknown) => {
      (original.onMouseLeave as ((e: unknown) => void) | undefined)?.(e);
      setAberto(false);
    },
    onFocus: (e: unknown) => {
      (original.onFocus as ((e: unknown) => void) | undefined)?.(e);
      setAberto(true);
    },
    onBlur: (e: unknown) => {
      (original.onBlur as ((e: unknown) => void) | undefined)?.(e);
      setAberto(false);
      setFixado(false);
    },
    onClick: (e: { stopPropagation: () => void }) => {
      (original.onClick as ((e: unknown) => void) | undefined)?.(e);
      // Toque/click alterna (mobile sem hover).
      e.stopPropagation();
      setFixado((v) => !v);
    },
  });

  return (
    <>
      {gatilho}
      {visivel &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={balaoRef}
            id={id}
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              maxWidth: pos?.maxWidth ?? 280,
              visibility: pos ? 'visible' : 'hidden',
              zIndex: CAMADA.DICA,
            }}
            className={cn(
              'pointer-events-none rounded-lg px-3 py-2',
              'bg-ink-1 dark:bg-paper-dark-surface text-paper dark:text-ink-dark-1',
              'text-xs font-medium leading-snug text-left normal-case tracking-normal shadow-lg',
              'whitespace-normal break-words',
              className,
            )}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
